import React, { useState, useRef, useEffect } from "react";
import { useMutation } from "@apollo/client";
import { RequestOtpDocument } from "../__generated__/requestOTP";
import { VerifyOtpDocument } from "../__generated__/verifyOTP";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { toast } from "sonner";

interface PhoneVerificationProps {
  phone: string;
  onVerificationSuccess: () => void;
  buttonClassName?: string;
  initialToken?: string;
  autoStart?: boolean;
}

const PhoneVerification: React.FC<PhoneVerificationProps> = ({
  phone,
  onVerificationSuccess,
  buttonClassName = "",
  initialToken,
  autoStart = false,
}) => {
  const [otpDigits, setOtpDigits] = useState(["", "", "", ""]);
  const [phoneToken, setPhoneToken] = useState("");
  const [showOTPInput, setShowOTPInput] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const hasAutoStarted = useRef(false);

  // Single ref array for inputs
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Phone OTP Request
  const [requestPhoneOTP, { loading: isRequestingOTP }] = useMutation(
    RequestOtpDocument,
    {
      variables: { input: { phone } },
      onError(error) {
        toast.error("Failed to send phone OTP", { description: error.message });
      },
      onCompleted(data) {
        if (data?.requestOTP) {
          setPhoneToken(String(data.requestOTP.token));
          setShowOTPInput(true);
          toast.success("OTP Sent", {
            description: "A 4-digit code has been sent to your phone via SMS and Whatsapp.",
          });
          setTimeout(() => inputRefs.current[0]?.focus(), 100);
        }
      },
    }
  );
  // Update token if initialToken changes or auto-request OTP on mount
  useEffect(() => {
    if (initialToken) {
      setPhoneToken(initialToken);
      setShowOTPInput(true);
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    } else if (autoStart && !hasAutoStarted.current && !showOTPInput && !phoneToken) {
      hasAutoStarted.current = true;
      requestPhoneOTP();
    }
   }, [initialToken, autoStart, showOTPInput, phoneToken, requestPhoneOTP, phone]);


  // Phone Verification
  const [verifyPhoneOTP, { loading: isVerifyingOTP }] = useMutation(
    VerifyOtpDocument,
    {
      onError(error) {
        toast.error("Failed to verify phone", {
          description: error.message,
        });
      },
      onCompleted(data) {
        if (data?.verifyOTP) {
          setIsVerified(true);
          toast.success("Phone verified successfully!");
          onVerificationSuccess();
        } else {
          toast.error("Phone verification failed. Please try again.");
        }
      },
    }
  );
//auto submission to prevent stable closures
useEffect(() => {
  const code = otpDigits.join("");
  if (code.length === 4 && !otpDigits.includes("") && phoneToken && !isVerified) {
    verifyPhoneOTP({
      variables: {
        input: {
          code,
          medium: "phone",
          token: phoneToken,
          update: true,
        },
      },
    });
  }
}, [otpDigits, phoneToken, isVerified, verifyPhoneOTP]);



  // Handle input change for OTP digits
  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otpDigits];
    newOtp[index] = value.slice(0, 1);
    setOtpDigits(newOtp);

    if (value && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };

 

  // Handle keydown for navigation between inputs and backspace
  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      if (otpDigits[index]) {
        const newOtp = [...otpDigits];
        newOtp[index] = "";
        setOtpDigits(newOtp);
      } else if (index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Handle OTP verification
  const handleManualVerify = () => {
    const code = otpDigits.join("");
    if (code.length !== 4) {
      toast.error("Please enter all 4 digits");
      return;
    }
    verifyPhoneOTP({
      variables: {
        input: {
          code,
          medium: "phone",
          token: phoneToken,
          update: true,
        },
      },
    });
  };

  // Handle paste functionality
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 4);
    if (pasted.length === 4) {
      setOtpDigits(pasted.split(""));
      inputRefs.current[3]?.focus();
    }
  };


  // Reset OTP inputs
  const resetOtp = () => {
    setOtpDigits(["", "", "", ""]);
    inputRefs.current[0]?.focus();
  };

  if (!showOTPInput) {
    return (
      <button
        onClick={() => requestPhoneOTP()}
        disabled={isRequestingOTP || isVerified}
        className={`flex justify-center items-center gap-2 ${buttonClassName}`}
      >
        {isRequestingOTP && (
          <AiOutlineLoading3Quarters className="h-5 w-5 animate-spin" />
        )}
        {isVerified
          ? "Phone Verified"
          : isRequestingOTP
          ? "Sending Code..."
          : "Verify Phone Number"}
      </button>
    );
  }

  return (
    <div className="mb-9">
   
      
      <div className="flex justify-center gap-2 my-4">
        {otpDigits.map((digit, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={1}
            value={digit}
            onFocus={(e) => e.target.select()}
            onChange={(e) => handleOtpChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            className="w-12 h-12 text-center text-lg font-semibold border border-gray-300 rounded-xl focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        ))}
      </div>

      <div className="flex items-center mb-9">
        <span className="text-body1 font-semi mr-2 mt-9">
          Didn't receive code?
        </span>
        <div
          className="text-buttonPrimary mt-9 text-body1 font-semi cursor-pointer"
          onClick={() => {
            resetOtp();
            requestPhoneOTP();
          }}
        >
          {isRequestingOTP ? "Please wait..." : "Resend"}
        </div>
      </div>

      <div className="my-5 w-full mb-9">
      <button
    type="button"
    className={`w-full flex justify-center py-4 px-4 border border-transparent text-body1 font-medium rounded-2xl focus:outline-none focus:ring-2 focus:ring-offset-2 text-white ${
      isVerifyingOTP || otpDigits.includes("") || isVerified
        ? "cursor-not-allowed bg-gray-300"
        : "bg-buttonPrimary"
    }`}
    disabled={isVerifyingOTP || otpDigits.includes("") || isVerified}
    onClick={handleManualVerify}
  >
    {isVerifyingOTP && (
      <AiOutlineLoading3Quarters className="h-6 w-6 animate-spin text-blue-400 mr-2" />
    )}
    {isVerified ? "Phone Verified ✓" : "Activate Account"}
  </button>

      </div>
    </div>
  );
};

export default PhoneVerification;
